import { DocumentTypeEnum } from 'api-hooks/order/travel-document';
import { Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import TableEditableComponent from 'components/elements/table/table';
import { ColumnDataType, IColumn } from 'components/elements/table/type';
import { differenceInDays } from 'date-fns';
import useGetVisaColumn from 'hooks/use-get-visa-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { addNewGuestDetail } from '../../hooks/use-get-default-values';
import {
  DocumentSaleFormType,
  DocumentSaleGuestDetailFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
  guestIndex: number;
  documentType: string;
  documentSaleStatus: {
    isSubmitted: boolean;
    isProccessed: boolean;
    isFinished: boolean;
  };
}

function useGetDetailTableList(
  val: {
    isSubmitted: boolean;
    isProccessed: boolean;
    isFinished: boolean;
  },
  parentIndex: number,
) {
  const { isFinished, isSubmitted } = val;
  const { t } = useTranslation();
  const { control } = useFormContext<DocumentSaleFormType>();
  const documentSaleName = `documentSales.${parentIndex}` as const;

  const [documentSale] = useWatch({
    control,
    name: [documentSaleName],
  });

  const { editable } = useFormState();
  const visaColumn = useGetVisaColumn();

  // MULTIPRICE
  // const { data } = useGetPreferenceGroups(
  //   {
  //     params: {
  //       type: 'booking',
  //       sub_type: PreferenceGroupSubTypeEnum.Visa,
  //       page: 1,
  //       limit: -1,
  //     },
  //   },
  //   {
  //     enabled: documentSale.documentType === DocumentTypeEnum.visa,
  //   },
  // );

  const columns = React.useMemo<IColumn<any>[]>(() => {
    return [
      ...(documentSale.documentType !== DocumentTypeEnum.visa
        ? [
            {
              header:
                documentSale.documentType === DocumentTypeEnum.apec_card
                  ? t('document:apec_type')
                  : t('common:type'),
              accessorKey: 'type',
              minSize: 100,
              dataType: ColumnDataType.select,
              disabled: isFinished,
              required: true,
              options:
                documentSale.documentType === DocumentTypeEnum.insurance
                  ? [
                      { id: 'duo', label: t('common:duo') },
                      { id: 'family', label: t('common:family') },
                      { id: 'group', label: t('common:group') },
                      { id: 'individual', label: t('common:individual') },
                    ]
                  : documentSale.documentType === DocumentTypeEnum.apec_card
                  ? [
                      {
                        label: t('common:new'),
                        id: 'new',
                      },
                      {
                        label: t('common:extend'),
                        id: 'extend',
                      },
                      {
                        label: t('common:change_passport'),
                        id: 'change_passport',
                      },
                    ]
                  : [
                      { id: 'electronic', label: t('common:electronic') },
                      { id: 'regular', label: t('common:regular') },
                    ],
            },
          ]
        : [
            {
              header: t('common:visa'),
              accessorKey: 'visaDocumentId',
              minSize: 300,
              dataType: ColumnDataType.select,
              disabled: isFinished || isSubmitted,
              required: true,
              selectProps: visaColumn,
            },
            {
              header: t('document:biometric'),
              accessorKey: 'biometric',
              minSize: 100,
              dataType: ColumnDataType.checkbox,
            },
            {
              header: t('document:appointment_date'),
              accessorKey: 'appointmentAt',
              minSize: 100,
              dataType: ColumnDataType.date,
              required: true,
            },
            {
              header: t('document:appointment_time'),
              accessorKey: 'appointmentAt',
              minSize: 20,
              dataType: ColumnDataType.time,
              required: true,
            },
          ]),
      ...(documentSale.documentType === DocumentTypeEnum.insurance
        ? [
            {
              header: t('common:started_at'),
              accessorKey: 'startAt',
              minSize: 100,
              disabled: isFinished,
              dataType: ColumnDataType.date,
              required: true,
            },
            {
              header: t('common:ended_at'),
              accessorKey: 'endAt',
              minSize: 100,
              disabled: isFinished,
              dataType: ColumnDataType.date,
              required: true,
            },
            {
              header: t('common:days'),
              accessorKey: 'days',
              minSize: 20,
              dataType: ColumnDataType.number,
              minNumber: 0,
              disabled: true,
            },
            {
              header: t('common:package'),
              accessorKey: 'package',
              minSize: 100,
              dataType: ColumnDataType.text,
              required: true,
            },
            {
              header: t('common:company'),
              accessorKey: 'company',
              minSize: 100,
              dataType: ColumnDataType.text,
              required: true,
            },
            {
              header: t('common:cover_area'),
              accessorKey: 'coverArea',
              minSize: 100,
              dataType: ColumnDataType.text,
              required: true,
            },
          ]
        : []),
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 300,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:price'),
        accessorKey: 'price',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        required: true,
      },
      ...(documentSale.documentType === DocumentTypeEnum.visa
        ? [
            ...(editable
              ? [
                  {
                    header: '',
                    accessorKey: 'delete',
                    minSize: 60,
                    size: 60,
                    maxSize: 60,
                    dataType: ColumnDataType.delete,
                  },
                ]
              : []),
          ]
        : []),
    ];
  }, [
    documentSale.documentType,
    editable,
    isFinished,
    isSubmitted,
    t,
    visaColumn,
  ]);

  return columns;
}

export default function DetailTable(props: Props) {
  const { guestIndex, documentSaleStatus, documentType, parentIndex } = props;
  const { editable } = useFormState();
  const { trigger, setValue, getValues } =
    useFormContext<DocumentSaleFormType>();
  const columns = useGetDetailTableList(documentSaleStatus, parentIndex);

  const name =
    `documentSales.${parentIndex}.documentSaleGuests.${guestIndex}.details` as const;

  // ignore Type instantiation is excessively deep and possibly infinite.
  // TODO: probably need check circular dependencies
  // @ts-ignore
  const dispatch = React.useCallback(
    (action) => {
      const currentRowIndex = action.rowIndex as number;
      const details = getValues(name);
      const detail = details[currentRowIndex];
      const columnId = action.columnId as keyof DocumentSaleGuestDetailFormType;
      const extra = action.extra as Value;
      const value = action.value;

      const otherVal = () => {
        if (columnId === 'startAt' || columnId === 'endAt') {
          try {
            const startAt = columnId === 'startAt' ? value : detail.startAt;
            const endAt = columnId === 'endAt' ? value : detail.endAt;
            const days = differenceInDays(endAt, startAt) + 1;
            if (days < 0 || !days) {
              return { days: 0 };
            } else {
              return { days };
            }
          } catch {
            return { days: 0 };
          }
        }

        if (columnId === 'visaDocumentId') {
          const price = extra?.[0]?.price as number | undefined;
          const visaDocumentId = extra?.[0]?.id as string | undefined;
          const label = extra?.[0]?.label as string | undefined;
          return {
            price: price || 0,
            visaDocumentId,
            label,
          };
        }
        return {};
      };

      if (action.type === 'addRow') {
        setValue(name, [
          ...details,
          addNewGuestDetail(documentType as DocumentTypeEnum),
        ]);
        trigger(name);
      } else if (action.type === 'removeRow') {
        details.splice(currentRowIndex, 1);
        setValue(name, details);
        trigger(name);
      } else if (action.type === 'update_cell') {
        details[currentRowIndex] = {
          ...detail,
          [action.columnId]: action.value,
          ...otherVal(),
        };
        setValue(name, details);
        trigger(name);
      }
    },
    // @ts-ignore
    [documentType, getValues, name, setValue, trigger],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableEditableComponent
        columns={columns}
        data={getValues(name)}
        dispatch={dispatch}
        disabled={!editable}
        noAddRemoveRow={documentType !== DocumentTypeEnum.visa}
        name={name}
        uniqueRowKey="customId"
        onCustomDisabledCell={({ accessorKey, rowOriginal }) => {
          if (!rowOriginal.biometric && accessorKey === 'appointmentAt') {
            return true;
          }
          return false;
        }}
      />
    </div>
  );
}
